/* eslint-disable sort-keys-fix/sort-keys-fix */

import { UserOrgTier } from '@acretrader/acremaps-permissions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDrawPolygon,
  faDrawSquare,
} from '@fortawesome/sharp-regular-svg-icons';
import { faDash, faHorizontalRule } from '@fortawesome/sharp-solid-svg-icons';
import { ReactNode } from 'react';

import { INavItemButton } from '@/components/button/navItemButton';
import {
  Barn,
  Buck,
  Building,
  CenterPivot,
  Circle,
  CrossHairs,
  DrawCircle,
  Fence,
  Grate,
  HouseChimney,
  Mark,
  Oil,
  Path,
  Pin,
  Pipeline,
  Pivot,
  Pump,
  Road,
  Solar,
  Square,
  Squares,
  Star,
  Storage,
  Text,
  Tracks,
  Well,
  Wind,
} from '@/components/icons/customizations';
import { getTailWindColors } from '@/utils/common';

const { 'customize-swatch': customColor } = getTailWindColors();
export interface IContent extends Omit<INavItemButton, 'onClick' | 'icon'> {
  requiredTier: UserOrgTier;
  customProperties?: {
    color?: string;
    size?: number;
    isSpecialLine?: boolean;
    pattern?: string | number[];
    opacity?: number;
    isPolygon?: boolean;
    'border-color'?: string;
    'font-type'?: string;
    'font-weight'?: string;
    'border-pattern-id'?: string;
  };
  mapToolTip?: string | null;
  mode: string;
  section?: string;
  toolId?: CustomizationType;
  id: string;
}

export enum Modes {
  DRAW_POLYGON = 'draw_polygon',
  DRAW_RECTANGLE = 'draw_rectangle',
  DRAW_CIRCLE = 'draw_circle',
  DRAW_PIVOT = 'draw_pivot',
  DRAW_POINT = 'draw_point',
  DRAW_CUSTOM_LINE = 'draw_custom_line',
  CLONE_BOUNDARY = 'simple_select',
}

export enum ShapeIds {
  POLYGON_DRAW = 'polygon',
  RECTANGLE_DRAW = 'polygon-rectangle',
  CIRCLE_DRAW = 'polygon-circle',
  PIVOT_DRAW = 'polygon-pivot',
  BOUNDARY_CLONE = 'clone-boundary',
}

export enum ShapeEditIds {
  MERGE_SHAPES = 'merge-shapes',
  SPLIT_SHAPES = 'split-shapes',
}

export enum PointIds {
  CIRCLE = 'circle',
  SQUARE = 'square',
  STAR = 'star',
  MARK = 'mark',
  PIN = 'pin',
  BUILDING = 'building',
  HOUSE = 'house',
  BARN = 'barn',
  STORAGE = 'grain_bin',
  WELL = 'well',
  HUNTING = 'hunting',
  PIVOT = 'pivot',
  GATE = 'gate',
  WIND = 'wind',
  OIL = 'oil',
  SOLAR = 'solar',
  EMPTY = 'point',
  PUMP = 'pump',
  BUCK = 'buck',
}

export enum SymbolLineIds {
  FENCE = 'fence',
  PIPELINE = 'pipeline',
  TRACKS = 'tracks',
  PATH = 'path',
}

export enum LineIds {
  LINE = 'line',
  ARROW = 'arrow',
  ARROWS = 'arrows',
  CREEK = 'creek',
  ROAD = 'road',
}

export enum NoteIds {
  TEXT = 'text',
  IMAGES = 'images',
}

export enum CustomizeLandConfigSections {
  SHAPES = 'shapes',
  // SHAPE_EDIT = 'shapeEdit',
  POINTS = 'points',
  LINES = 'lines',
  ANNOTATE = 'annotate',
}

export type CustomizationType =
  | ShapeIds
  | ShapeEditIds
  | PointIds
  | LineIds
  | NoteIds
  | SymbolLineIds;

export enum LINE_TYPES {
  DASHED = 'dashed',
  DOTTED = 'dotted',
  DOUBLE = 'double',
  SOLID = 'solid',
}

export enum FontTypes {
  COMIC_NEUE = 'Comic Neue',
  EB_GARAMOND = 'EB Garamond',
  MERRIWEATHER = 'Merriweather',
  MONTSERRAT = 'Montserrat',
  OPEN_SANS = 'Open Sans',
  OSWALD = 'Oswald',
  ROBOTO = 'Roboto',
  ROBOTO_CONDENSED = 'Roboto Condensed',
  POPPINS = 'Poppins',
}

export enum FontWeights {
  REGULAR = 'regular',
  BOLD = 'bold',
}

export const fontWeightMap: Record<
  FontWeights,
  { label: string; id: FontWeights }
> = {
  [FontWeights.REGULAR]: {
    id: FontWeights.REGULAR,
    label: 'Regular',
  },
  [FontWeights.BOLD]: {
    id: FontWeights.BOLD,
    label: 'Bold',
  },
};

export const fontTypesMap: Record<
  FontTypes,
  { mapboxValue: string; id: string }
> = {
  [FontTypes.EB_GARAMOND]: {
    id: 'eb-garamond',
    mapboxValue: 'EB Garamond',
  },
  [FontTypes.MERRIWEATHER]: {
    id: 'merriweather',
    mapboxValue: 'Merriweather',
  },
  [FontTypes.OPEN_SANS]: {
    id: 'open-sans',
    mapboxValue: 'Open Sans',
  },
  [FontTypes.ROBOTO]: {
    id: 'roboto',
    mapboxValue: 'Roboto',
  },
  [FontTypes.ROBOTO_CONDENSED]: {
    id: 'roboto-condensed',
    mapboxValue: 'Roboto Condensed',
  },
  [FontTypes.OSWALD]: {
    id: 'oswald',
    mapboxValue: 'Oswald',
  },
  [FontTypes.POPPINS]: {
    id: 'poppins',
    mapboxValue: 'Poppins',
  },
  [FontTypes.MONTSERRAT]: {
    id: 'montserrat',
    mapboxValue: 'Montserrat',
  },
  [FontTypes.COMIC_NEUE]: {
    id: 'comic-neue',
    mapboxValue: 'Comic Neue',
  },
};

export const lineTypesMap = {
  [LINE_TYPES.DASHED]: [5, 5],
  [LINE_TYPES.DOTTED]: [1, 3],
  [LINE_TYPES.DOUBLE]: 'DOUBLE',
  [LINE_TYPES.SOLID]: [1, 0],
};

type Section = {
  content: Partial<Record<CustomizationType, IContent>>;
  title: string;
};

export const Tooltips = {
  CLOSE_SHAPE: 'Double-click or press Enter to close',
  CLICK_PIVOT: {
    0: 'Click to set center',
    1: '',
    2: 'Click to finish',
  },
  CLICK_TO_SET_POINT: 'Click to set point',
  CIRCLE_DRAW_TOOLTIP: 'Click your center point and drag',
  CLICK_AND_DRAG: 'Click and drag',
  CLONE_BOUNDARY: 'Select boundaries to clone',
};

export type CustomizeLandConfig = Record<CustomizeLandConfigSections, Section>;

const pointDefault = {
  'border-color': customColor.black,
  color: customColor.white,
};

export const customizeIconMap: Partial<Record<CustomizationType, ReactNode>> = {
  [ShapeIds.POLYGON_DRAW]: <FontAwesomeIcon icon={faDrawPolygon} />,
  [ShapeIds.RECTANGLE_DRAW]: <FontAwesomeIcon icon={faDrawSquare} />,
  [ShapeIds.CIRCLE_DRAW]: <DrawCircle />,
  [ShapeIds.PIVOT_DRAW]: <Pivot />,
  [ShapeIds.BOUNDARY_CLONE]: <Squares />,
  [PointIds.CIRCLE]: <Circle className="h-full w-full" />,
  [PointIds.SQUARE]: <Square className="h-full w-full" />,
  [PointIds.STAR]: <Star className="h-full w-full" />,
  [PointIds.MARK]: <Mark className="h-full w-full" />,
  [PointIds.PIN]: <Pin className="h-full w-full" />,
  [PointIds.BUILDING]: <Building className="h-full w-full" />,
  [PointIds.HOUSE]: <HouseChimney className="h-full w-full" />,
  [PointIds.BARN]: <Barn className="h-full w-full" />,
  [PointIds.STORAGE]: <Storage className="h-full w-full" />,
  [PointIds.WELL]: <Well className="h-full w-full" />,
  [PointIds.HUNTING]: <CrossHairs className="h-full w-full" />,
  [PointIds.PIVOT]: <CenterPivot className="h-full w-full" />,
  [PointIds.GATE]: <Grate className="h-full w-full" />,
  [PointIds.WIND]: <Wind className="h-full w-full" />,
  [PointIds.OIL]: <Oil className="h-full w-full" />,
  [PointIds.SOLAR]: <Solar className="h-full w-full" />,
  [PointIds.EMPTY]: <div />,
  [PointIds.PUMP]: <Pump className="h-full w-full" />,
  [PointIds.BUCK]: <Buck className="h-full w-full" />,
  [LineIds.LINE]: <FontAwesomeIcon icon={faHorizontalRule} />,
  [LineIds.ROAD]: <Road />,
  [SymbolLineIds.FENCE]: <Fence />,
  [LineIds.CREEK]: <FontAwesomeIcon icon={faDash} />,
  [SymbolLineIds.PIPELINE]: <Pipeline />,
  [SymbolLineIds.TRACKS]: <Tracks />,
  [SymbolLineIds.PATH]: <Path />,
  [NoteIds.TEXT]: <Text />,
};

export const configIdMap: Partial<Record<CustomizationType, IContent>> = {
  [ShapeIds.POLYGON_DRAW]: {
    id: ShapeIds.POLYGON_DRAW,
    name: 'Polygon',
    requiredTier: UserOrgTier.FREE,
    mode: Modes.DRAW_POLYGON,
    mapToolTip: Tooltips.CLICK_TO_SET_POINT,
    customProperties: {
      color: customColor.white,
      opacity: 0.2,
      isPolygon: true,
      'border-pattern-id': LINE_TYPES.SOLID,
    },
  },
  [ShapeIds.RECTANGLE_DRAW]: {
    id: ShapeIds.RECTANGLE_DRAW,
    name: 'Rectangle',
    requiredTier: UserOrgTier.FREE,
    mode: Modes.DRAW_RECTANGLE,
    mapToolTip: Tooltips.CLICK_AND_DRAG,
    customProperties: {
      color: customColor.white,
      opacity: 0.2,
      isPolygon: true,
      'border-pattern-id': LINE_TYPES.SOLID,
    },
  },
  [ShapeIds.CIRCLE_DRAW]: {
    id: ShapeIds.CIRCLE_DRAW,
    name: 'Circle',
    mode: Modes.DRAW_CIRCLE,
    mapToolTip: Tooltips.CIRCLE_DRAW_TOOLTIP,
    requiredTier: UserOrgTier.FREE,
    customProperties: {
      color: customColor.white,
      opacity: 0.2,
      isPolygon: true,
      'border-pattern-id': LINE_TYPES.SOLID,
    },
  },
  [ShapeIds.PIVOT_DRAW]: {
    id: ShapeIds.PIVOT_DRAW,
    name: 'Pivot',
    mode: Modes.DRAW_PIVOT,
    mapToolTip: Tooltips.CLICK_PIVOT[0],
    requiredTier: UserOrgTier.FREE,
    customProperties: {
      color: customColor.white,
      opacity: 0.2,
      isPolygon: true,
      'border-pattern-id': LINE_TYPES.SOLID,
    },
  },
  [ShapeIds.BOUNDARY_CLONE]: {
    id: ShapeIds.BOUNDARY_CLONE,
    name: 'Clone Boundary',
    mode: Modes.CLONE_BOUNDARY,
    requiredTier: UserOrgTier.FREE,
    mapToolTip: Tooltips.CLONE_BOUNDARY,
    customProperties: {
      color: customColor.white,
      opacity: 0.2,
      isPolygon: true,
      'border-pattern-id': LINE_TYPES.SOLID,
    },
  },
  [PointIds.CIRCLE]: {
    customProperties: pointDefault,
    id: PointIds.CIRCLE,
    name: 'Dot',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.SQUARE]: {
    customProperties: pointDefault,
    id: PointIds.SQUARE,
    name: 'Square',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.STAR]: {
    customProperties: pointDefault,
    id: PointIds.STAR,
    name: 'Star',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.MARK]: {
    customProperties: pointDefault,
    id: PointIds.MARK,
    name: 'Mark',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.PIN]: {
    customProperties: pointDefault,
    id: PointIds.PIN,
    name: 'Pin',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.BUILDING]: {
    customProperties: pointDefault,
    id: PointIds.BUILDING,
    name: 'Building',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.HOUSE]: {
    customProperties: pointDefault,
    id: PointIds.HOUSE,
    name: 'House',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.BARN]: {
    customProperties: pointDefault,
    id: PointIds.BARN,
    name: 'Barn',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.STORAGE]: {
    customProperties: pointDefault,
    id: PointIds.STORAGE,
    name: 'Grain Storage',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.WELL]: {
    customProperties: pointDefault,
    id: PointIds.WELL,
    name: 'Well',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.HUNTING]: {
    customProperties: pointDefault,
    id: PointIds.HUNTING,
    name: 'Hunting',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.PIVOT]: {
    customProperties: pointDefault,
    id: PointIds.PIVOT,
    name: 'Pivot',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.GATE]: {
    customProperties: pointDefault,
    id: PointIds.GATE,
    name: 'Gate',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.WIND]: {
    customProperties: pointDefault,
    id: PointIds.WIND,
    name: 'Wind',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.OIL]: {
    customProperties: pointDefault,
    id: PointIds.OIL,
    name: 'Oil',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [PointIds.SOLAR]: {
    customProperties: pointDefault,
    id: PointIds.SOLAR,
    name: 'Solar',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
  },
  [LineIds.LINE]: {
    id: LineIds.LINE,
    name: 'Line',
    mode: Modes.DRAW_CUSTOM_LINE,
    requiredTier: UserOrgTier.FREE,
    mapToolTip: Tooltips.CLICK_TO_SET_POINT,
    customProperties: {
      'border-pattern-id': LINE_TYPES.SOLID,
    },
  },
  [LineIds.ROAD]: {
    id: LineIds.ROAD,
    name: 'Road',
    mode: Modes.DRAW_CUSTOM_LINE,
    requiredTier: UserOrgTier.FREE,
    mapToolTip: Tooltips.CLICK_TO_SET_POINT,
    customProperties: {
      size: 5,
    },
  },
  [SymbolLineIds.FENCE]: {
    id: SymbolLineIds.FENCE,
    name: 'Fence',
    mode: Modes.DRAW_CUSTOM_LINE,
    requiredTier: UserOrgTier.FREE,
    mapToolTip: Tooltips.CLICK_TO_SET_POINT,
    customProperties: {
      isSpecialLine: true,
    },
  },
  [LineIds.CREEK]: {
    id: LineIds.CREEK,
    name: 'Creek',
    mode: Modes.DRAW_CUSTOM_LINE,
    requiredTier: UserOrgTier.FREE,
    mapToolTip: Tooltips.CLICK_TO_SET_POINT,
    customProperties: { size: 4, 'border-color': customColor.blue },
  },
  [SymbolLineIds.PIPELINE]: {
    id: SymbolLineIds.PIPELINE,
    name: 'Pipeline',
    mode: Modes.DRAW_CUSTOM_LINE,
    mapToolTip: Tooltips.CLICK_TO_SET_POINT,
    requiredTier: UserOrgTier.FREE,
    customProperties: {
      isSpecialLine: true,
      'border-color': customColor.black,
    },
  },
  [SymbolLineIds.TRACKS]: {
    id: SymbolLineIds.TRACKS,
    name: 'Tracks',
    mode: Modes.DRAW_CUSTOM_LINE,
    mapToolTip: Tooltips.CLICK_TO_SET_POINT,
    requiredTier: UserOrgTier.FREE,
    customProperties: {
      isSpecialLine: true,
    },
  },
  [SymbolLineIds.PATH]: {
    id: SymbolLineIds.PATH,
    name: 'Path',
    mode: Modes.DRAW_CUSTOM_LINE,
    mapToolTip: Tooltips.CLICK_TO_SET_POINT,
    requiredTier: UserOrgTier.FREE,
    customProperties: {
      isSpecialLine: true,
    },
  },
  [NoteIds.TEXT]: {
    id: NoteIds.TEXT,
    name: 'Text',
    mode: Modes.DRAW_POINT,
    requiredTier: UserOrgTier.FREE,
    customProperties: {
      color: customColor.white,
      'border-color': 'transparent',
      'font-type': fontTypesMap[FontTypes.OPEN_SANS].id,
      'font-weight': fontWeightMap[FontWeights.BOLD].id,
      size: 20,
    },
  },
};

export const customizeLandConfig: CustomizeLandConfig = {
  [CustomizeLandConfigSections.SHAPES]: {
    content: {
      [ShapeIds.POLYGON_DRAW]: configIdMap[ShapeIds.POLYGON_DRAW],
      [ShapeIds.RECTANGLE_DRAW]: configIdMap[ShapeIds.RECTANGLE_DRAW],
      [ShapeIds.CIRCLE_DRAW]: configIdMap[ShapeIds.CIRCLE_DRAW],
      [ShapeIds.PIVOT_DRAW]: configIdMap[ShapeIds.PIVOT_DRAW],
      [ShapeIds.BOUNDARY_CLONE]: configIdMap[ShapeIds.BOUNDARY_CLONE],
    },
    title: 'Shapes',
  },
  [CustomizeLandConfigSections.ANNOTATE]: {
    content: {
      [NoteIds.TEXT]: configIdMap[NoteIds.TEXT],
    },
    title: 'Notes',
  },
  // Design contains an extra divider within shapes. By leaving title empty, you get that effect and it still appears like these belong to Shapes
  // [CustomizeLandConfigSections.SHAPE_EDIT]: {
  //   content: {
  //     [ShapeEditIds.MERGE_SHAPES]: {
  //       id: ShapeEditIds.MERGE_SHAPES,
  //       icon: <MergeShapes />,
  //       name: 'Merge Shapes',
  //       requiredTier: UserOrgTier.FREE,
  //     },
  //     [ShapeEditIds.SPLIT_SHAPES]: {
  //       id: ShapeEditIds.SPLIT_SHAPES,
  //       icon: <Split />,
  //       name: 'Split Shapes',
  //       requiredTier: UserOrgTier.FREE,
  //     },
  //   },
  //   title: '',
  // },
  [CustomizeLandConfigSections.POINTS]: {
    content: {
      [PointIds.CIRCLE]: configIdMap[PointIds.CIRCLE],
      [PointIds.SQUARE]: configIdMap[PointIds.SQUARE],
      [PointIds.STAR]: configIdMap[PointIds.STAR],
      [PointIds.MARK]: configIdMap[PointIds.MARK],
      [PointIds.PIN]: configIdMap[PointIds.PIN],
      [PointIds.BUILDING]: configIdMap[PointIds.BUILDING],
      [PointIds.HOUSE]: configIdMap[PointIds.HOUSE],
      [PointIds.BARN]: configIdMap[PointIds.BARN],
      [PointIds.STORAGE]: configIdMap[PointIds.STORAGE],
      [PointIds.WELL]: configIdMap[PointIds.WELL],
      [PointIds.HUNTING]: configIdMap[PointIds.HUNTING],
      [PointIds.PIVOT]: configIdMap[PointIds.PIVOT],
      [PointIds.GATE]: configIdMap[PointIds.GATE],
      [PointIds.WIND]: configIdMap[PointIds.WIND],
      [PointIds.OIL]: configIdMap[PointIds.OIL],
      [PointIds.SOLAR]: configIdMap[PointIds.SOLAR],
    },
    title: 'Points',
  },
  [CustomizeLandConfigSections.LINES]: {
    content: {
      [LineIds.LINE]: configIdMap[LineIds.LINE],
      [LineIds.ROAD]: configIdMap[LineIds.ROAD],
      [SymbolLineIds.FENCE]: configIdMap[SymbolLineIds.FENCE],
      [LineIds.CREEK]: configIdMap[LineIds.CREEK],
      [SymbolLineIds.PIPELINE]: configIdMap[SymbolLineIds.PIPELINE],
      [SymbolLineIds.TRACKS]: configIdMap[SymbolLineIds.TRACKS],
      [SymbolLineIds.PATH]: configIdMap[SymbolLineIds.PATH],
    },
    title: 'Lines',
  },
};
